import { Box, Group, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'

import LayersIcon from '@/assets/icons/layers.svg'
import ParlayIcon from '@/assets/icons/parlay.svg'

interface CompactCardFooterProps {
  /** 卡片已展示之外的额外 market/outcome 数。 */
  moreCount: number
  /** `event.combos_enabled` → 左侧渲染 parlay 标记。 */
  combosEnabled?: boolean
  /**
   * "+N more" 名词（cdc-web 同款）：`markets`（match 卡）/ `outcomes`
   * （futures 卡）。单复数走 ICU。
   */
  countNoun?: 'markets' | 'outcomes'
  /** 成交量槽位（VolumeLabel；无数据时自隐藏）。 */
  volumeSlot?: ReactNode
  onClick?: () => void
}

/**
 * Adapted from cdc-web `Compact/_shared/CompactCardFooter.tsx`。
 *
 * 差异（均已决策）：
 * - 本区没有事件详情路由 → "+N more …" 从链接降级为纯文本
 *   （`href` / ViewTransitionLink 不抄），布局/门控逻辑 1:1 保留；
 * - parlay.svg 两仓逐字节一致，直接复用。
 *
 * 布局：`[ volume ]  [ parlay ]  ················  [ layers · +N more … ]`
 * （WWW-5071：+N more 前有 layers 堆叠图标，设计稿常显；parlay 仍按
 * `combos_enabled` 门控。）三项皆无 → 整行不渲染。
 */
export function CompactCardFooter({
  moreCount,
  combosEnabled = false,
  countNoun = 'markets',
  volumeSlot,
}: Readonly<CompactCardFooterProps>) {
  const t = useTranslations('HomePageV2.Prediction')

  const hasMore = moreCount > 0
  const showParlay = combosEnabled
  const hasLeftCluster = !!volumeSlot || showParlay
  if (!hasMore && !hasLeftCluster) return null

  return (
    <Group gap="2xs" align="center" wrap="nowrap" w="100%">
      {hasLeftCluster && (
        <Group gap="2xs" align="center" wrap="nowrap" style={{ flexShrink: 0 }}>
          {volumeSlot}
          {showParlay && (
            // SVG 用 currentColor 着色；Mantine 的 `c` 样式传不到裸 SVGR 组件，
            // 由外层 Box 设 color（原注释保留）。
            <Box
              lh={0}
              style={{ color: 'var(--content-secondary)', flexShrink: 0 }}
              role="img"
              aria-label="Parlay eligible"
            >
              <ParlayIcon width={16} height={16} aria-hidden="true" />
            </Box>
          )}
        </Group>
      )}
      {hasMore && (
        <Group
          gap="2xs"
          align="center"
          wrap="nowrap"
          style={{ marginInlineStart: 'auto', flexShrink: 0 }}
        >
          {/* 设计稿 WWW-5071：+N more 前有「堆叠/更多市场」layers 图标（og-web
              combo.svg 同款三层堆叠；cdc-web 实现里也没有，属设计稿新增项）。
              与 parlay（combos 标记，受 combos_enabled 门控）是不同图标。 */}
          <Box lh={0} style={{ color: 'var(--content-secondary)', flexShrink: 0 }}>
            <LayersIcon width={16} height={16} aria-hidden="true" />
          </Box>
          <Text variant="micro-text" c="var(--content-secondary)" ta="end">
            {countNoun === 'outcomes'
              ? t('moreOutcomes', { count: moreCount })
              : t('moreMarkets', { count: moreCount })}
          </Text>
        </Group>
      )}
    </Group>
  )
}
